import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import type { TimekeepingReport } from "@/lib/api";

const TASK_COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f97316", "#10b981", "#eab308", "#06b6d4", "#64748b"];

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: 13,
};

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

export function TimekeepingAnalytics({ data, isLoading }: { data?: TimekeepingReport; isLoading?: boolean }) {
  const { t } = useTranslation();

  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader><CardTitle>{t("timekeeping.analytics.title", { defaultValue: "Timekeeping" })}</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-64 w-full" /></CardContent>
      </Card>
    );
  }

  const weekData = data.hoursByWeek.map((w) => ({ week: w.week.slice(5), hours: w.hours }));
  const taskData = data.hoursByTask.slice(0, 8);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("timekeeping.analytics.title", { defaultValue: "Timekeeping" })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label={t("timekeeping.analytics.totalHours", { defaultValue: "Total hours" })} value={data.workforce.totalHours} />
          <Kpi label={t("timekeeping.analytics.avgPerEmployee", { defaultValue: "Avg / employee" })} value={data.workforce.avgHoursPerEmployee} />
          <Kpi label={t("timekeeping.analytics.submittedPct", { defaultValue: "Submitted %" })} value={`${data.compliance.submittedPct}%`} />
          <Kpi label={t("timekeeping.analytics.approvedPct", { defaultValue: "Approved %" })} value={`${data.compliance.approvedPct}%`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-medium mb-2">{t("timekeeping.analytics.hoursByWeek", { defaultValue: "Hours by week" })}</div>
            {weekData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.noData", { defaultValue: "No data" })}</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={weekData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={40} />
                  <Tooltip contentStyle={tooltipStyle} labelStyle={{ fontWeight: 600 }} />
                  <Line type="monotone" dataKey="hours" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3, fill: "#6366f1" }} activeDot={{ r: 5 }} name="Hours" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div>
            <div className="text-sm font-medium mb-2">{t("timekeeping.analytics.hoursByTask", { defaultValue: "Hours by task" })}</div>
            {taskData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.noData", { defaultValue: "No data" })}</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={taskData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="task" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={110} />
                  <Tooltip contentStyle={tooltipStyle} labelStyle={{ fontWeight: 600 }} />
                  <Bar dataKey="hours" radius={[0, 4, 4, 0]} name="Hours">
                    {taskData.map((_, i) => <Cell key={i} fill={TASK_COLORS[i % TASK_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
